import React from 'react'
import { connect } from 'react-redux'

import { BucketSource, ProjectIdentity, RootStateTypeExtra } from '@store/types'

import DataHandler from '@components/data-handler'
import Skeleton from '@components/skeleton/v2'

import UnitInfoCard from '@pages/properties-view/apartments/v2/unit-view/unit-info-card'

import { selectFromResult, useGetUnitGalleryQuery } from '@api/unit-gallery'

import { CarouselProvider } from '@adUtilities/components/carousel-provider'
import { SlideInterface } from '@adUtilities/constants/carousel'
import { FileType } from '@adUtilities/constants/common'
import mapPropertyGallery, {
  FloorImage,
  PropertyGallery,
} from '@adUtilities/property-gallery'
import { Unit } from '@adUtilities/types/apartment'

interface ExtendedSlideInterface extends SlideInterface {
  originalSrc: string
}

export interface ComparableUnitItemProps {
  unit: Unit
  projectIdentity: ProjectIdentity
  showInfo: boolean
  fullScreenMode?: boolean
  onlyShowPropertyGalleries?: boolean
}

function ComparableUnitItem({
  unit,
  projectIdentity,
  showInfo,
  fullScreenMode,
  onlyShowPropertyGalleries,
}: ComparableUnitItemProps) {
  const { projectUUID, gallerySettings, projectName } = projectIdentity
  const { type: effectType, intervalInSeconds: autoPlayIntervalInSeconds } =
    gallerySettings || {}

  const unitGalleryQuery = useGetUnitGalleryQuery(
    { projectName, unitId: unit.id },
    { selectFromResult }
  )
  const { unitGalleryData } = unitGalleryQuery

  const buildPropertyGalleryItems = (): PropertyGallery[] =>
    unitGalleryData?.map(({ id, src, label, type, thumbnail }) => ({
      id,
      src,
      thumbnail,
      label,
      type,
      noSpliceUrl: false,
    })) || []

  const buildCarouselItems = (
    propertyGallery: PropertyGallery[]
  ): ExtendedSlideInterface[] =>
    propertyGallery.map(({ id, label, type, src, thumbnail, noSpliceUrl }) => ({
      id,
      label,
      src: type === FileType.Image && src ? src : thumbnail || '',
      thumbnail,
      originalSrc: src,
      bucketType: BucketSource.Legacy,
      fileType: type,
      noSpliceUrl,
    }))

  const slides = React.useMemo(() => {
    const {
      metas: { floorImage, floorLabel, floorPdf, viewLineImages },
    } = unit

    const floorItems: FloorImage[] = floorImage
      ? [
          {
            id: floorPdf,
            src: floorImage,
            label: floorLabel || floorImage,
          },
        ]
      : []

    const propertyGalleryItems = buildPropertyGalleryItems()

    const propertyGallery = mapPropertyGallery({
      projectId: projectUUID,
      floorImages: floorItems,
      viewLineImages: viewLineImages || [],
      propertyGalleries: propertyGalleryItems,
      onlyShowPropertyGallery: Boolean(onlyShowPropertyGalleries),
    })
    const carouselItems = buildCarouselItems(propertyGallery)

    return carouselItems[0] ? [carouselItems[0]] : []
  }, [projectUUID, unit, unitGalleryData, onlyShowPropertyGalleries])

  return (
    <DataHandler
      payloads={{
        ...unitGalleryQuery,
        data: slides,
      }}
      skeletonFrame={<Skeleton containerClass="!w-full absolute" />}
    >
      <div className="relative flex h-full w-full">
        <div
          className={`h-92 pointer-events-none absolute bottom-0 z-20 w-full bg-shadow-horizontal opacity-75 transition duration-300 ease-in ${
            showInfo ? 'translate-y-0' : 'translate-y-full'
          }`}
        >
          <div
            className={`flex h-full transform items-stretch duration-200 ease-in ${
              fullScreenMode ? 'pb-2' : 'pb-12'
            } pr-5`}
          >
            <div className="ml-auto self-end">
              <UnitInfoCard unit={unit} alignRight />
            </div>
          </div>
        </div>
        <div className="absolute inset-0">
          <CarouselProvider
            gallerySettings={{ effectType, autoPlayIntervalInSeconds }}
            slides={slides}
            activeSlideIndex={0}
            showControl={false}
            mousewheel={false}
            autoPlay={false}
            toggleControls={false}
          />
        </div>
      </div>
    </DataHandler>
  )
}

export default connect(
  ({ projectIdentity, firestore: { session } }: RootStateTypeExtra) => ({
    projectIdentity,
    showInfo: !!session?.building?.unitInfoPopup,
    onlyShowPropertyGalleries: projectIdentity.onlyShowPropertyGalleries,
  })
)(ComparableUnitItem)
